import { useEffect, useId, useState } from 'react';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PercentQuestionProps {
  question: string;
  hint?: string;
  /** Sufijo del catálogo (`%`); se pinta dentro del campo, no en la etiqueta. */
  suffix?: string;
  value: number | undefined;
  /** `undefined` cuando lo escrito no es un entero del rango: nunca sube un valor inválido. */
  onChange: (value: number | undefined) => void;
  min?: number;
  max?: number;
  required?: boolean;
}

const DEFAULT_MIN = 0;

const DEFAULT_MAX = 100;

/**
 * Un entero del rango, o `undefined`. Sólo dígitos: el motor guarda un `int`, así que
 * ni decimales ni signo — y `Number('')` es 0, que colaría el vacío como respuesta.
 */
const parse = (text: string, min: number, max: number): number | undefined => {
  if (!/^\d+$/.test(text)) return undefined;

  const value = Number(text);

  return value >= min && value <= max ? value : undefined;
};

/**
 * Pregunta de porcentaje: un entero con sufijo, validado contra un rango.
 *
 * **Lo escrito y la respuesta son cosas distintas.** El campo conserva lo tecleado
 * —borrarlo por detrás mientras se escribe es desconcertante— pero hacia arriba sólo
 * sale un valor del rango o `undefined`, así que una respuesta fuera de rango no
 * existe y la puerta del paso no puede dejarla pasar. Si el valor cambia desde fuera
 * (una poda que lo limpia), el campo se resincroniza.
 *
 * Es genérica del motor, no de Lift: es el primer input numérico del wizard.
 */
export const PercentQuestion = ({
  question,
  hint,
  suffix,
  value,
  onChange,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  required,
}: PercentQuestionProps) => {
  const { t } = useTranslation();
  const labelId = useId();
  const hintId = useId();
  const errorId = useId();
  const [draft, setDraft] = useState(value === undefined ? '' : String(value));

  const committed = parse(draft, min, max);

  useEffect(() => {
    if (committed !== value) setDraft(value === undefined ? '' : String(value));
    // Sólo resincronizamos ante un cambio de FUERA: incluir `committed` o `draft`
    // aquí borraría lo tecleado en cuanto deja de ser válido.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Escribir algo que no es del rango no es un error todavía si el campo está vacío:
  // ahí sólo está sin responder, y de eso ya avisa la puerta del paso.
  const error = draft !== '' && committed === undefined;

  const handleChange = (text: string) => {
    setDraft(text);
    onChange(parse(text, min, max));
  };

  return (
    <FormControl variant="standard" error={error} fullWidth sx={{ mb: 3 }}>
      <FormLabel
        id={labelId}
        sx={{
          typography: 'textMd',
          fontWeight: 600,
          color: 'text.primary',
          p: 0,
          mb: hint ? 0.5 : 1.5,
          '&.Mui-focused, &.Mui-error': { color: 'text.primary' },
        }}
      >
        {question}
        {required && (
          <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
            *
          </Box>
        )}
      </FormLabel>
      {hint && (
        <Typography
          id={hintId}
          variant="textSm"
          sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}
        >
          {hint}
        </Typography>
      )}
      <OutlinedInput
        aria-labelledby={labelId}
        // El motivo del error tiene que colgar del input: MUI le pone `aria-invalid`
        // solo, y sin esto un lector de pantalla anuncia "inválido" y nada más.
        aria-describedby={
          [hint ? hintId : null, error ? errorId : null]
            .filter(Boolean)
            .join(' ') || undefined
        }
        value={draft}
        onChange={(event) => handleChange(event.target.value)}
        inputProps={{ inputMode: 'numeric', maxLength: 3 }}
        endAdornment={
          suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : undefined
        }
        sx={{ maxWidth: 160, borderRadius: 1.5 }}
      />
      {error && (
        <FormHelperText id={errorId} sx={{ mt: 1, ml: 0 }}>
          {t('wizard.errors.percentRange', { min, max })}
        </FormHelperText>
      )}
    </FormControl>
  );
};